// src/onboarding/onboardingStore.js
//
// The personalization status as a MODULE store (decision 5): ONE poller (useOnboardingPoller,
// mounted by AppShell) writes it; the gate, the banner, the badge, the wizard and the pages that
// must refresh after a run read it. With an empty store every reader renders nothing — which is
// why the suites that render TenantAdminLayout / BrandVoicePage need no new mocks.
// ⭐ The snapshot is REPLACED on every change (useSyncExternalStore compares by reference).
import { useSyncExternalStore } from 'react';

export const EMPTY_SNAPSHOT = Object.freeze({
  tenantId: null,
  status: null,
  lastGood: null,
  error: null,
  personalizable: null,
  personalizableChecked: false,
  outcome: null,
  pendingStart: false,
});

let snapshot = EMPTY_SNAPSHOT;
let finishedEpoch = 0;
let refresher = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* a bad subscriber must not break the poller */ }
  });
}

export function subscribeOnboarding(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function getOnboardingSnapshot() {
  return snapshot;
}

export function getRunFinishedEpoch() {
  return finishedEpoch;
}

/** Merge a patch into a NEW snapshot. */
export function patchOnboarding(patch) {
  snapshot = { ...snapshot, ...patch };
  emit();
}

/** Start describing another workspace (or none) from scratch. */
export function resetOnboarding(tenantId = null) {
  snapshot = { ...EMPTY_SNAPSHOT, tenantId };
  emit();
}

/** A run ended (decision 16): every subscriber re-reads what the run rewrote. */
export function bumpRunFinished() {
  finishedEpoch += 1;
  emit();
}

export function dismissOnboardingOutcome() {
  if (!snapshot.outcome) return;
  patchOnboarding({ outcome: null });
}

/** The poller registers its tick here; anyone may ask for an immediate re-read. */
export function registerOnboardingRefresher(fn) {
  refresher = fn;
  return () => { if (refresher === fn) refresher = null; };
}

/** Ask the poller to read NOW (after a form submit, a 409 RUN_ACTIVE…). No-op when no poller runs. */
export function requestOnboardingRefresh() {
  if (refresher) refresher();
}

/** Tests only: module state outlives a test, so every suite that drives the store calls this in beforeEach. */
export function __resetOnboardingStoreForTests() {
  snapshot = EMPTY_SNAPSHOT;
  finishedEpoch = 0;
  refresher = null;
  emit();
}

export function useOnboarding() {
  return useSyncExternalStore(subscribeOnboarding, getOnboardingSnapshot, getOnboardingSnapshot);
}

export function useRunFinishedEpoch() {
  return useSyncExternalStore(subscribeOnboarding, getRunFinishedEpoch, getRunFinishedEpoch);
}
